import { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import API_BASE_URL from '../../config/api';
import EventList from './EventList';
import OrderList from './OrderList';
import OrderForm from './OrderForm';
import OrderDetail from './OrderDetail';
import {
  Package, AlertTriangle, IndianRupee, Truck,
  Store, CalendarDays, ChevronRight,
} from 'lucide-react';

const OB_FONT_STYLE = `
  .ob-root, .ob-root * {
    font-family: 'DM Sans', sans-serif !important;
  }
`;

const OrderBook = () => {
  const { authTokens } = useContext(AuthContext);
  const [view, setView] = useState('home');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSource, setOrderSource] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const authAxios = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${authTokens?.access}` },
  }), [authTokens]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await authAxios.get('/api/order-book/dashboard/');
      setDashboard(res.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const goBack = () => {
    if (view === 'order-detail') {
      if (selectedEvent) setView('event-orders');
      else if (orderSource === 'direct') setView('direct-orders');
      else setView('home');
    } else if (view === 'order-form') {
      if (selectedEvent) setView('event-orders');
      else if (orderSource === 'direct') setView('direct-orders');
      else setView('home');
    } else if (view === 'event-orders') {
      setSelectedEvent(null);
      setView('events');
    } else if (view === 'events' || view === 'direct-orders') {
      setOrderSource(null);
      setView('home');
    } else {
      setView('home');
    }
  };

  const handleOrderSaved = () => {
    fetchDashboard();
    goBack();
  };

  // ─── CHILD VIEWS ──────────────────────────────────────────────
  if (view === 'direct-orders') {
    return (
      <div className="ob-root"><style>{OB_FONT_STYLE}</style>
        <OrderList authAxios={authAxios} source="direct" title="Direct Orders"
          onBack={goBack}
          onOrderClick={(order) => { setSelectedOrder(order); setView('order-detail'); }}
          onNewOrder={() => setView('order-form')} />
      </div>
    );
  }

  if (view === 'events') {
    return (
      <div className="ob-root"><style>{OB_FONT_STYLE}</style>
        <EventList authAxios={authAxios} onBack={goBack}
          onEventClick={(event) => { setSelectedEvent(event); setView('event-orders'); }} />
      </div>
    );
  }

  if (view === 'event-orders') {
    return (
      <div className="ob-root"><style>{OB_FONT_STYLE}</style>
        <OrderList authAxios={authAxios} source="exhibition" eventId={selectedEvent?.id}
          title={selectedEvent?.name || 'Event'} onBack={goBack}
          onOrderClick={(order) => { setSelectedOrder(order); setView('order-detail'); }}
          onNewOrder={() => setView('order-form')} />
      </div>
    );
  }

  if (view === 'order-detail') {
    return (
      <div className="ob-root"><style>{OB_FONT_STYLE}</style>
        <OrderDetail authAxios={authAxios} orderId={selectedOrder?.id || selectedOrder}
          onBack={goBack} onRefresh={fetchDashboard} />
      </div>
    );
  }

  if (view === 'order-form') {
    return (
      <div className="ob-root"><style>{OB_FONT_STYLE}</style>
        <OrderForm authAxios={authAxios}
          source={orderSource === 'exhibition' ? 'exhibition' : 'direct'}
          event={selectedEvent} onBack={goBack} onSaved={handleOrderSaved} />
      </div>
    );
  }

  // ─── HOME VIEW ────────────────────────────────────────────────
  const directCount = (dashboard?.source_stats || [])
    .filter(s => !['exhibition', 'popup', 'trunk_show'].includes(s.source))
    .reduce((sum, s) => sum + (s.count || 0), 0);
  const directPending = (dashboard?.source_stats || [])
    .filter(s => !['exhibition', 'popup', 'trunk_show'].includes(s.source))
    .reduce((sum, s) => sum + Number(s.pending_amount || 0), 0);
  const eventCount = (dashboard?.source_stats || [])
    .filter(s => ['exhibition', 'popup', 'trunk_show'].includes(s.source))
    .reduce((sum, s) => sum + (s.count || 0), 0);
  const eventPending = (dashboard?.source_stats || [])
    .filter(s => ['exhibition', 'popup', 'trunk_show'].includes(s.source))
    .reduce((sum, s) => sum + Number(s.pending_amount || 0), 0);

  const stats = [
    { label: 'Total Orders', value: dashboard?.total_orders || 0, icon: Package, bg: 'bg-slate-100', iconColor: 'text-slate-600' },
    { label: 'Payment Due', value: dashboard?.payment_due?.count || 0, sub: `₹${Number(dashboard?.payment_due?.amount || 0).toLocaleString('en-IN')}`, icon: IndianRupee, bg: 'bg-amber-50', iconColor: 'text-amber-600' },
    { label: 'Overdue', value: dashboard?.overdue_delivery || 0, icon: AlertTriangle, bg: 'bg-red-50', iconColor: 'text-red-500' },
    { label: 'Ready', value: dashboard?.ready_to_deliver || 0, icon: Truck, bg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
  ];

  return (
    <div className="ob-root"><style>{OB_FONT_STYLE}</style>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Order Book</h2>
        <p className="text-gray-500 text-base mt-1">Track all your offline orders</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-10 h-10 border-[3px] border-gray-200 border-t-gray-800 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                  <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 tracking-tight">{stat.value}</div>
                  <div className="text-sm text-gray-500 mt-0.5 font-medium">{stat.label}</div>
                  {stat.sub && <div className="text-sm font-semibold text-amber-600 mt-1">{stat.sub}</div>}
                </div>
              );
            })}
          </div>

          {/* Category Cards */}
          <div className="space-y-4">
            {/* Direct Orders */}
            <button
              onClick={() => { setOrderSource('direct'); setView('direct-orders'); }}
              className="w-full bg-white rounded-2xl border border-gray-100 p-5 text-left hover:shadow-md active:scale-[0.98] transition-all shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Store className="w-7 h-7 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-bold text-gray-900">Direct Orders</div>
                  <div className="text-sm text-gray-500 mt-0.5 leading-snug">
                    Store walk-ins, WhatsApp, phone calls, Instagram, referrals
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
              </div>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                <div className="flex-1">
                  <span className="text-2xl font-bold text-gray-900">{directCount}</span>
                  <span className="text-sm text-gray-500 ml-1.5">orders</span>
                </div>
                {directPending > 0 && (
                  <div className="bg-amber-50 px-3 py-1.5 rounded-full">
                    <span className="text-sm font-semibold text-amber-700">₹{directPending.toLocaleString('en-IN')} due</span>
                  </div>
                )}
              </div>
            </button>

            {/* Exhibition & Pop-up Orders */}
            <button
              onClick={() => { setOrderSource('exhibition'); setView('events'); }}
              className="w-full bg-white rounded-2xl border border-gray-100 p-5 text-left hover:shadow-md active:scale-[0.98] transition-all shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <CalendarDays className="w-7 h-7 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-bold text-gray-900">Exhibition & Pop-up</div>
                  <div className="text-sm text-gray-500 mt-0.5 leading-snug">
                    Create events, track orders inside them
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
              </div>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                <div className="flex-1">
                  <span className="text-2xl font-bold text-gray-900">{eventCount}</span>
                  <span className="text-sm text-gray-500 ml-1.5">orders</span>
                  <span className="text-sm text-gray-400 ml-2">·</span>
                  <span className="text-sm text-gray-500 ml-2">{dashboard?.event_count || 0} events</span>
                </div>
                {eventPending > 0 && (
                  <div className="bg-amber-50 px-3 py-1.5 rounded-full">
                    <span className="text-sm font-semibold text-amber-700">₹{eventPending.toLocaleString('en-IN')} due</span>
                  </div>
                )}
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderBook;
